'use client';

import { useState, useRef, useEffect } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown, FileSpreadsheet, FileText } from 'lucide-react';
import { Button } from '../Button/Button';
import type { ButtonProps } from '../Button/Button';
import styles from './ExportDropdown.module.css';

export interface ExportDropdownProps {
	onExportExcel: () => void;
	onExportPdf: () => void;
	className?: string;
	buttonClassName?: string;
	variant?: ButtonProps['variant'];
	label?: string;
	icon?: ReactNode;
	showChevron?: boolean;
	dropdownPlacement?: 'bottom' | 'top';
	disabled?: boolean;
}

export const ExportDropdown = ({
	onExportExcel,
	onExportPdf,
	className,
	buttonClassName,
	variant = 'secondary',
	label = 'Exportar',
	icon,
	showChevron = true,
	dropdownPlacement = 'bottom',
	disabled = false,
}: ExportDropdownProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const wrapperRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isOpen) return;
		const handleClickOutside = (e: MouseEvent) => {
			if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isOpen]);

	const handleExcel = () => {
		if (disabled) return;
		setIsOpen(false);
		onExportExcel();
	};

	const handlePdf = () => {
		if (disabled) return;
		setIsOpen(false);
		onExportPdf();
	};

	return (
		<div className={`${styles.wrapper} ${className ?? ''}`} ref={wrapperRef}>
			<Button
				disabled={disabled}
				variant={variant}
				className={`${styles.button} ${buttonClassName ?? ''}`}
				onClick={() => setIsOpen((prev) => !prev)}
			>
				{icon}
				{label}
				{showChevron && (
					<ChevronDown className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`} />
				)}
			</Button>
			{isOpen && (
				<div
					className={`${styles.dropdown} ${dropdownPlacement === 'top' ? styles.dropdownTop : ''}`}
				>
					<button type="button" className={styles.option} onClick={handleExcel} disabled={disabled}>
						<FileSpreadsheet className={styles.icon} />
						Excel (.xlsx)
					</button>
					<button type="button" className={styles.option} onClick={handlePdf} disabled={disabled}>
						<FileText className={styles.icon} />
						PDF (.pdf)
					</button>
				</div>
			)}
		</div>
	);
};
